import {useMemo} from 'react';

import {useCrossProjectRunsRunsQuery} from '../../../generated/graphql';
import {
  extractRunFilterErrorMessage,
  propagateErrorsContext,
} from '../../../util/errors';
import {
  Or,
  runContainsFilter,
  runRegexFilter,
  toMongo,
} from '../../../util/filters';

export type RunOption = {
  id: string;
  name: string;
  displayName: string | null;
};

export const useCrossProjectRuns = (
  projectInternalId: string,
  searchQuery: string,
  isRegexSearch: boolean
): {
  loading: boolean;
  runs: RunOption[];
  totalCount: number;
  runFilterErrorMessage?: string;
  loadFailed: boolean;
} => {
  const filters = useMemo(() => {
    if (!searchQuery) {
      return undefined;
    }
    const filterFn = isRegexSearch ? runRegexFilter : runContainsFilter;
    const filter = Or([
      filterFn(searchQuery),
      filterFn(searchQuery, 'run', 'displayName'),
    ]);
    return JSON.stringify(toMongo(filter));
  }, [searchQuery, isRegexSearch]);

  const {data, loading, error} = useCrossProjectRunsRunsQuery({
    variables: {
      internalId: projectInternalId,
      filters,
    },
    context: propagateErrorsContext(),
  });

  const runs = useMemo(
    () =>
      data?.project?.runs?.edges.map(e => ({
        id: e.node.id,
        name: e.node.name,
        displayName: e.node.displayName ?? null,
      })) ?? [],
    [data]
  );

  const totalCount = data?.project?.runs?.totalCount ?? 0;

  const runFilterErrorMessage = extractRunFilterErrorMessage(error);

  return {
    loading,
    runs,
    totalCount,
    runFilterErrorMessage,
    loadFailed: error != null && runFilterErrorMessage == null,
  };
};
